import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CardSkeleton, ListSkeleton } from "@/components/Skeletons";
import { Ticket, Clock, CheckCircle2, TrendingUp, ArrowUpRight, Zap, Users, Star, Activity } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_TONE = {
  open: "bg-primary/10 text-primary border-primary/30",
  in_progress: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  waiting_staff: "bg-primary/10 text-primary border-primary/30",
  waiting_customer: "bg-accent/10 text-accent border-accent/30",
  solved: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  closed: "bg-muted text-muted-foreground border-border",
  reopened: "bg-accent/10 text-accent border-accent/30",
};

function StatCard({ icon: Icon, label, value, sub, tone = "primary" }) {
  return (
    <div className="p-5 rounded-lg border border-border/60 bg-card hover:border-primary/40 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${tone === "primary" ? "bg-primary/10 text-primary" : tone === "accent" ? "bg-accent/10 text-accent" : "bg-emerald-500/10 text-emerald-500"}`}>
          <Icon className="h-4 w-4" />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      </div>
      <div className="font-display font-black tracking-tighter text-3xl">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const role = user?.role;

  useEffect(() => {
    const url = role === "admin" ? "/dashboard/admin" : role === "agent" ? "/dashboard/staff" : "/dashboard/customer";
    api.get(url).then((r) => setData(r.data)).catch(() => setData({}));
  }, [role]);

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{Array.from({length:4}).map((_,i)=><CardSkeleton key={i}/>)}</div>
        <ListSkeleton rows={4} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-primary mb-2">// Overview</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Welcome, {user?.name?.split(" ")[0] || "there"}.</h1>
          <p className="text-sm text-muted-foreground mt-1">Here's what's happening across your support surface.</p>
        </div>
        <Button data-testid="dashboard-new-ticket" asChild><Link to="/tickets/new">Create ticket</Link></Button>
      </header>

      {role === "admin" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Users" value={data.total_users} tone="primary" />
            <StatCard icon={Ticket} label="Total tickets" value={data.total_tickets} tone="accent" />
            <StatCard icon={Zap} label="Agents" value={data.agents} tone="primary" sub={`${data.admins} admins`} />
            <StatCard icon={Activity} label="Announcements" value={data.active_announcements} tone="emerald" />
          </div>
          <section className="p-6 rounded-lg border border-border/60 bg-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg">Tickets — last 7 days</h2>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">daily</span>
            </div>
            <div className="flex items-end gap-2 h-32">
              {(data.daily_tickets || []).map((d) => {
                const max = Math.max(1, ...(data.daily_tickets || []).map((x) => x.count));
                const h = (d.count / max) * 100;
                return (
                  <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-primary/20 rounded-t" style={{ height: `${h}%`, minHeight: 4 }} />
                    <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{d.day.slice(5)}</div>
                    <div className="text-xs">{d.count}</div>
                  </div>
                );
              })}
              {(data.daily_tickets || []).length === 0 && <div className="text-sm text-muted-foreground">No ticket activity yet.</div>}
            </div>
          </section>
        </>
      )}

      {role === "agent" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Ticket} label="Open" value={data.open} sub={`${data.in_progress} in progress`} />
            <StatCard icon={Clock} label="Awaiting staff" value={data.waiting_staff} tone="accent" />
            <StatCard icon={CheckCircle2} label="Solved today" value={data.solved_today} tone="emerald" />
            <StatCard icon={Star} label="CSAT" value={`${data.csat}★`} sub={`${data.csat_count} ratings`} tone="primary" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <section className="md:col-span-2 p-6 rounded-lg border border-border/60 bg-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-lg">Live activity</h2>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">realtime</span>
              </div>
              <ul className="space-y-3 max-h-[380px] overflow-y-auto pr-2">
                {(data.activity || []).map((e) => (
                  <li key={e.event_id} className="flex items-start gap-3 text-sm border-b border-border/40 pb-3">
                    <span className="h-2 w-2 mt-1.5 rounded-full bg-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div><span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{e.event_type.replace(/_/g," ")}</span></div>
                      <div className="truncate">{e.actor_name} · <span className="text-muted-foreground">{e.actor_role}</span></div>
                    </div>
                    <span className="font-mono text-[10px] text-muted-foreground">{formatDistanceToNow(new Date(e.created_at), { addSuffix: true })}</span>
                  </li>
                ))}
                {(data.activity || []).length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
              </ul>
            </section>
            <section className="p-6 rounded-lg border border-border/60 bg-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-lg">Leaderboard</h2>
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
              <ol className="space-y-3">
                {(data.leaderboard || []).map((a, i) => (
                  <li key={a.user_id} className="flex items-center gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground w-6">#{i+1}</span>
                    <span className="flex-1 truncate text-sm">{a.name}</span>
                    <span className="font-display font-black text-lg">{a.solved}</span>
                  </li>
                ))}
                {(data.leaderboard || []).length === 0 && <div className="text-sm text-muted-foreground">No solved tickets yet.</div>}
              </ol>
            </section>
          </div>
        </>
      )}

      {role === "customer" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard icon={Ticket} label="Open tickets" value={data.open} />
            <StatCard icon={CheckCircle2} label="Solved" value={data.solved} tone="emerald" />
            <StatCard icon={Activity} label="Total" value={data.total} tone="accent" />
          </div>
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-lg">Recent tickets</h2>
              <Link to="/tickets" className="text-xs font-mono uppercase tracking-widest text-primary hover:underline">All tickets →</Link>
            </div>
            <div className="space-y-2">
              {(data.recent || []).map((t) => (
                <Link key={t.ticket_id} to={`/tickets/${t.ticket_id}`}
                  className="flex items-center gap-4 p-4 rounded-lg border border-border/60 bg-card hover:border-primary/40 transition-colors">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground w-16 shrink-0">{t.ticket_number}</span>
                  <span className="flex-1 truncate text-sm">{t.subject}</span>
                  <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded border ${STATUS_TONE[t.status] || ""}`}>{t.status.replace(/_/g," ")}</span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
              {(data.recent || []).length === 0 && (
                <div className="p-8 text-center rounded-lg border border-dashed border-border/60 text-sm text-muted-foreground">
                  No tickets yet. <Link to="/tickets/new" className="text-primary underline">Create your first</Link>.
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
